/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnInit, inject} from '@angular/core';
import * as _ from 'lodash';

import template from './team-list-page.component.html';
import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../../common/controller/signal-admin-resource-list-state';
import {OgTranslateService} from '../../common/filters/og-translate.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {getTargetingType} from '../../targeting/targeting-constants';
import {getCountryName} from '../../common/directives/country-select/countries';
import {TeamApiService, TeamModel} from './team-api.service';

interface TeamFeeRow {
  label: string;
  fee: any;
}

@Component({
  standalone: false,
  selector: 'adn-team-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamListPageComponent implements OnInit {
  readonly itemStateName = 'app.adm.teams.team';
  readonly columns: SignalAdminListColumn[];

  readonly list = inject(SignalAdminResourceListStateFactory).create<TeamModel>(
    inject(TeamApiService),
    {listId: 'fullTeamList'},
    inject(DestroyRef)
  );

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly ogTranslate: OgTranslateService
  ) {
    this.columns = [
      {param: 'name'},
      {param: 'type'},
      {param: 'externalReference'}
    ];
    if (this.LocalNetworkProfile.isPureMarketplacePlatform()) {
      this.columns.push(
        {param: '', label: 'targeting', sortable: false},
        {param: '', label: 'fees', sortable: false},
        {param: 'country'}
      );
    }
  }

  @Input()
  set readOnly(value: boolean) {
    this.list.readOnly.set(value);
  }

  hasColumn(label: string): boolean {
    return this.columns.some((column) => (column.label || column.param) === label);
  }

  countryName(country: string): string {
    return getCountryName(country);
  }

  getTargetTranslation(apiValue: string): string {
    return getTargetingType(apiValue).title;
  }

  getTeamFeeRows(team: any): TeamFeeRow[] {
    const rows: TeamFeeRow[] = [];
    if (team.techFeeCpm) {
      rows.push({label: this.ogTranslate.transform('techFee'), fee: team.techFeeCpm});
    }
    if (team.creativeTagTechFeeCpm) {
      rows.push({label: this.ogTranslate.transform('creativeTagTechFee'), fee: team.creativeTagTechFeeCpm});
    }
    _.forEach(team.supplementaryFees, (sFee: any) => {
      rows.push({label: sFee.name + ' (Supp.) Fee', fee: sFee});
    });
    _.forEach(team.targetingTypeTechFees, (fee: any, key: string) => {
      rows.push({label: this.getTargetTranslation(key) + ' Fee', fee});
    });
    _.forEach(team.layoutFees, (lFee: any) => {
      rows.push({label: _.get(lFee, ['layout', 'name']) + ' Fee', fee: lFee.fee});
    });
    return rows;
  }

  visibleTeamFeeRows(team: any, feeRows?: TeamFeeRow[]): TeamFeeRow[] {
    const rows = feeRows || this.getTeamFeeRows(team);
    if (team.showAllFees || rows.length <= 3) {
      return rows;
    }
    return rows.slice(0, 3);
  }

  ngOnInit(): void {
    this.list.load();
  }
}
